import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getEocConfirmations } from '../../api/documents';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

type EocConfirmation = string[];

interface EocConfirmationsState {
  eocConfirmations: EocConfirmation;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: EocConfirmationsState = {
  eocConfirmations: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchEocConfirmations = createAsyncThunk<
  EocConfirmation,
  number,
  { state: { eocConfirmations: EocConfirmationsState }; rejectValue: string }
>('eocConfirmations/fetchEocConfirmations', async (company_id, { getState, rejectWithValue }) => {
  const { eocConfirmations, lastFetched } = getState().eocConfirmations;

  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
    return eocConfirmations;
  }

  try {
    const eocConfirmationsData: EocConfirmation = await getEocConfirmations(company_id);
    return eocConfirmationsData;
  } catch (err) {
    if (err instanceof Error) {
      console.error('Error fetching EOC confirmations:', err.message);
      return rejectWithValue(err.message);
    } else {
      console.error('Unknown error fetching EOC confirmations');
      return rejectWithValue('An unknown error occurred');
    }
  }
});

const eocConfirmationsSlice = createSlice({
  name: 'eocConfirmations',
  initialState,
  reducers: {
    resetEocConfirmationsState: (state) => {
      state.eocConfirmations = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEocConfirmations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEocConfirmations.fulfilled, (state, action: PayloadAction<EocConfirmation>) => {
        state.loading = false;
        state.eocConfirmations = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchEocConfirmations.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'An unknown error occurred';
        console.error('Error in state:', state.error);
      });
  },
});

export const { resetEocConfirmationsState } = eocConfirmationsSlice.actions;
export default eocConfirmationsSlice.reducer;
